import { describeError, useApi } from '@classgo/app-kit';
import { Badge, Card, EmptyState, Tabs } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';
import { AnnouncementsManage } from './manage';

/**
 * The school's published announcements, newest first.
 */
export const AnnouncementsScreen = () => {
  const t = useT();
  const { session } = useTenant();
  const [tab, setTab] = useState('feed');
  const manages =
    session.permissions.all || session.permissions.keys.includes('communication:write');
  if (!manages) return <AnnouncementsFeed />;
  return (
    <Tabs
      label={t('announcements.title')}
      value={tab}
      onChange={setTab}
      items={[
        { id: 'feed', label: t('announcements.tab.feed'), content: <AnnouncementsFeed /> },
        { id: 'manage', label: t('announcements.tab.manage'), content: <AnnouncementsManage /> },
      ]}
    />
  );
};

/**
 * The published announcements as everyone reads them.
 */
const AnnouncementsFeed = () => {
  const t = useT();
  const api = useApi();
  const announcements = useQuery(recordQueries.announcements(api));
  const audience = (kind: string, name: string | null) =>
    kind === 'all'
      ? t('announcements.audience.all')
      : kind === 'sede'
        ? t('announcements.audience.sede', { name: name ?? '' })
        : kind === 'grade'
          ? t('announcements.audience.grade', { name: name ?? '' })
          : t('announcements.audience.group', { name: name ?? '' });
  return (
    <Card title={t('announcements.title')} flush>
      {announcements.isError ? (
        <div className="p-6">
          <EmptyState
            title={t('entity.error.title')}
            description={describeError(t, announcements.error)}
          />
        </div>
      ) : announcements.isPending ? (
        <p className="p-6 text-sm text-gray-500">{t('common.loading')}</p>
      ) : (announcements.data ?? []).length === 0 ? (
        <div className="p-6">
          <EmptyState
            title={t('announcements.empty.title')}
            description={t('announcements.empty.body')}
          />
        </div>
      ) : (
        <ul className="divide-y divide-gray-100" aria-label={t('announcements.title')}>
          {(announcements.data ?? []).map((item) => (
            <li key={item.id} className="px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900">{item.title}</h3>
                <Badge tone={item.audience === 'all' ? 'brand' : 'info'}>
                  {audience(item.audience, item.targetName)}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-gray-700">{item.body}</p>
              <p className="mt-2 text-xs text-gray-500">
                {t('announcements.by', {
                  author: item.authorName ?? '—',
                  date: item.publishedAt
                    ? new Date(item.publishedAt).toLocaleDateString('es-CO', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })
                    : '',
                })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
